import { Activity } from './activity';
import { Employee } from './employee';
import { Project } from './project';
import { Tower } from './tower';

export interface SearchOptions<T> {
  value: string;
  field?: T;
}

export interface FiltersFormData {
  startDate: Date;
  endDate: Date;
  engineer: Employee | null;
  manager: Employee | null;
  project: Project | null;
  tower: Tower | null;
  monthSelect: MonthSelect;
}

export interface ReportDetails {
  comment: string;
  date: string;
  employeeId: string;
  employeeName: string;
  hours: number;
  id: string;
  task: {
    id: string;
    name: string;
    projectId: string;
    project: {
      id: string;
      name: string;
      code: string;
    };
  };
}

export interface GetEmployeesReportsParams {
  userRoleId?: string;
  managerId?: string;
  startDate?: string;
  endDate?: string;
  take?: number;
  skip?: number;
  includeDeleted?: boolean;
  includeInactive?: boolean;
  showHierarchy?: boolean;
}

export interface GetSummaryPayload {
  startDate: string | Date;
  endDate: string | Date;
  managerId?: string | null;
  employeeId?: string | null;
  projectId?: string | null;
  skip?: number | null;
  take?: number | null;
  filter?: string | null;
  includeInactive?: boolean;
  includeDeleted?: boolean;
  showHierarchy?: boolean;
  monthSelect?: MonthSelect;
  towerId?: string | null;
}

export interface EmployeesSummary {
  employee: {
    id: string;
    internalId: string;
    email: string;
    name: string;
    department: string;
    userName: string;
    managerId: string;
    managerName: string;
    jobTitle: string;
    isActive: boolean;
    location: string;
    employeeType: string;
    workingHours: number;
    permissionGroups: [
      {
        id: string;
        name: string;
      }
    ];
  };
  workingHours: number;
  loggedHours: number;
  loggedMDs: number;
}

export interface ProjectsSummary {
  project: {
    id: string;
    name: string;
    code: string;
    ongoing: boolean;
    projectOwnerId: string;
    projectOwner: Employee;
    salesManagerId: string;
    salesManager: Employee;
    clientId: string;
    client: null | unknown;
    tower: Tower | null;
  };
  loggedMDs: number;
}

export interface EmployeeAllocations {
  employee: Employee;
  allocations: [
    {
      project: Project;
      percentage: number;
    }
  ];
  totalAllocationPercentage: number;
}

export interface ProjectAllocations {
  project: Project;
  employeeProjectAllocations: [
    {
      employee: Employee;
      hoursOnProject: number;
      percentageOnProject: number;
      totalLoggedHours: number;
      totalWorkingHours: number;
    }
  ];
  totalHours: number;
}

export interface GetReportsActivitiesPayload {
  startDate: string;
  endDate: string;
  employeeId: string | null;
  projectId: string | null;
  roleId: string;
}

export interface ReportsActivitiesResponse {
  activities: Activity[];
  workingHours: number;
}
export type SearchQueryParams = {
  employeeId?: string;
  managerId?: string;
  projectId?: string;
  startDate?: string;
  endDate?: string;
  monthSelect?: MonthSelect;
  userRoleId?: string;
  viewMode?: ViewMode;
};

export enum InputFormat {
  SEPARATOR_SPACE = 'dd MMM yyyy',
  SEPARATOR_FORWARD_SLASH = 'dd/MM/yyyy',
  YEAR = 'yyyy',
}

export const ReportsField = {
  PROJECT: 'project',
  MANAGER: 'manager',
  ENGINEER: 'engineer',
  START_DATE: 'startDate',
  END_DATE: 'endDate',
  MONTH_SELECT: 'monthSelect',
  TOWER: 'tower',
} as const;

export enum MonthSelect {
  CURRENT = 'Current',
  PREVIOUS = 'Previous',
  CUSTOM = 'Custom',
}

export interface ReportFilters {
  startDate: string;
  endDate: string;
  managerId?: string | null;
  managerName?: string | null;
  employeeName?: string | null;
  employeeLocation?: string;
  projectName?: string | null;
  employeeId: string | null;
  projectId: string | null;
  projectCode?: string | null;
  towerId?: string | null;
  towerName?: string | null;
  skip?: number | null;
  monthSelect: MonthSelect;
  showHierarchy?: boolean;
  includeInactive?: boolean;
  includeDeleted?: boolean;
}

export interface ExportReportFilters extends ReportFilters {
  reportSource?: ReportSource;
}

export enum ViewMode {
  PEOPLE = 'People',
  PROJECT = 'Project',
}

export enum ReportSource {
  PEOPLE = 1,
  PROJECT = 2,
}
